import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/dist/query';

export interface Product {
  id: string;
  materialId: string;
  productCategoryId: string;
  productId: string;
  countryOfOrigin: string;
  productName: string;
  status: string;
}

interface Step {
  name: string;
  status: string;
  stepname: string;
}

// Form data interface for add/edit operations
export interface ProductFormData {
  [key: string]: any;
  // Purchasing & Supplier
  product_id: string;
  product_description: string;
  purchasing_status: string;
  supplier_part_number_id: string;
  supplier_lead_time_id: string;
  supplier_id: string;

  
  // General Data
  product_category: string;
  base_uom: string;
  dc_license: string;
  alternative_uom: string;
  numerator: string;
  denominator: string;
  hws_portfolio_id: string;
  product_type_id: string;
  
  // Product Dimensioning
  gross_weight: string;
  gross_volume: string;
  net_weight: GLfloat;
  net_volume: GLfloat;
  net_weight_unit: string;
  net_volume_unit: string;
  gross_weight_unit: string;
  gross_volume_unit: string;
  kclength: GLfloat;
  width: GLfloat;
  height: GLfloat;
  length_unit: string;
  width_unit: string;
  height_unit: string;
  dimension_unit: string;
  
  // Logistics
  loading_group: string;
  transportation_group: string;
  
  // F&C
  material_group: string;
  
  // Product Lifecycle Milestone (all as date fields)
  concept_date: string;
  ramp_up_date: string;
  volume_production_date: string;
  phase_out_date: string;
  discontinue_date: string;
  no_longer_manufactured_date: string;
  no_longer_deliverable_date: string;
  obsolete_date: string;
}

export type ProductMode = 'add' | 'edit' | 'add-edit' | 'view';

export interface ProductMasterState {
  productList: Product[];
  totalProductCount : number;
  // Form state for add/edit operations
  mode: ProductMode;
  formData: ProductFormData;
  validations: Record<string, { isValid: boolean; errorMessage: string }>;
  isLoading: boolean;
  error: string | null;
  editingProductId: string | null;
  showDialogBox: boolean | false;
  bulkUploadData: {};
  productType:string;
  columnFilter: any;
  totalProductTypeCount: {},
  stepData: Step[];
  supplierData: { value: string; label: string }[];
  categoryListData: { value: string; label: string }[];
  
  // Dynamic list values loaded from API
  baseUomList?: { value: string; label: string }[];
  dangerousGoodsClassification?: { value: string; label: string }[];
  dangerousGoodsUnNumberList?: { value: string; label: string }[];
  grossVolumeUnit?: { value: string; label: string }[];
  grossWeightUnit?: { value: string; label: string }[];
  netVolumeUnit?: { value: string; label: string }[];
  netWeightUnit?: { value: string; label: string }[];
  serialNumberProfile?: { value: string; label: string }[];
  productStatus?: { value: string; label: string }[];
  widthUnit?: { value: string; label: string }[];
  heightUnit?: { value: string; label: string }[];
  kclengthUnit?: { value: string; label: string }[];
  minimumOrderQuantityUnit?: { value: string; label: string }[];
}

const initialFormData: ProductFormData = {
  // Purchasing & Supplier
  product_id: '',
  product_description: '',
  purchasing_status: '',
  purchasing_status_text: 'in_preparation',
  supplier_part_number_id: '',
  supplier_id: '',
  supplier_lead_time_id: '',
  
  // General Data
  product_category: '',
  base_uom: 'EA',
  base_uom_id: 'EA',
  dc_license: '',
  alternative_uom: '',
  numerator: '',
  denominator: '',
  hws_portfolio_id: '',
  product_type_id: '',
  
  // Product Dimensioning
  gross_weight: "",
  gross_weight_unit: '',
  gross_volume: "",
  gross_volume_unit: '',
  net_weight: 0.001,
  net_weight_unit: '',
  net_volume: 0.001,
  net_volume_unit: '',
  kclength: 0.001,
  length_unit: '',
  width: 0.001,
  width_unit: '',
  height: 0.001,
  height_unit: '',
  dimension_unit: '',
  
  // Logistics
  loading_group: '',
  transportation_group: '',
  
  // F&C
  material_group: '',
  
  // Product Lifecycle Milestone
  concept_date: '',
  ramp_up_date: '',
  volume_production_date: '',
  phase_out_date: '',
  discontinue_date: '',
  no_longer_manufactured_date: '',
  no_longer_deliverable_date: '',
  obsolete_date: '',
};

const initialState: ProductMasterState = {
  productList: [],
  totalProductCount : 0,
  // Form state
  mode: 'add',
  formData: initialFormData,
  validations: {},
  isLoading: false,
  error: null,
  editingProductId: null,
  showDialogBox: false,
  bulkUploadData: {},
  productType:"",
  columnFilter: {
    product_type_id: {
      filter: "",
      sort: "",
      checkempty: false,
      checkNonEmpty: false,
      filterCheck: false
    },
    id: {
      filter: "",
      sort: "",
      checkempty: false,
      checkNonEmpty: false,
      filterCheck: false
    },
    product_description: {
      filter: "",
      sort: "",
      checkempty: false,
      checkNonEmpty: false,
      filterCheck: false
    },
    gic_l_code: {
      filter: "",
      sort: "",
      checkempty: false,
      checkNonEmpty: false,
      filterCheck: false
    },
    product_lifecycle_status: {
      filter: "",
      sort: "",
      checkempty: false,
      checkNonEmpty: false,
      filterCheck: false
    },
    createdAt: {
      filter: [],
      sort: "",
      checkempty: false,
      checkNonEmpty: false,
      filterCheck: false
    },
    created_by: {
      filter: "",
      sort: "",
      checkempty: false,
      checkNonEmpty: false,
      filterCheck: false
    },
    updated_by: {
      filter: [],
      sort: "",
      checkempty: false,
      checkNonEmpty: false,
      filterCheck: false
    },
    product_sub_type_id: {
      filter: "",
      sort: "",
      checkempty: false,
      checkNonEmpty: false,
      filterCheck: false
    },
    commercial_model_id: {
      filter: "",
      sort: "",
      checkempty: false,
      checkNonEmpty: false,
      filterCheck: false
    },
    product_source_id: {
      filter: "",
      sort: "",
      checkempty: false,
      checkNonEmpty: false,
      filterCheck: false
    },
    network_support_id: {
      filter: "",
      sort: "",
      checkempty: false,
      checkNonEmpty: false,
      filterCheck: false
    }
  },
  totalProductTypeCount: {},
  stepData: [{
      name: "Purchasing & supplier info", 
      status: "current",
      stepname: "purchasing_supplier"
    },
    {
      name: "General data",
      status: "future",
      stepname: "general_data"
    },
    {
      name: "Product dimensioning",
      status: "future", 
      stepname: "product_dimensioning"
    },
    {
      name: "Logistics",
      status: "future",
      stepname: "logistics"
    },
    {
      name: "Finance and Control",
      status: "future",
      stepname: "fc"
    },
    {
      name: "Product lifecycle milestones",
      status: "future",
      stepname: "product_lifecycle"
    }],
  supplierData: [],
  categoryListData : []
};

const productMasterSlice = createSlice({
  name: 'productMaster',
  initialState,
  reducers: {
    // Existing action - keep as is
    setNokiaProducts(state, action) {
      const data = action.payload
      state.productList = action.payload;
    },
    setNokiaProductsCount(state,action){
      const data = action.payload
      state.totalProductTypeCount = action.payload;
    },
    setNokiaProductsTotalCount(state,action){
      const data = action.payload
      state.totalProductCount = action.payload;
    },
    // Form management actions
    setMode(state, action: PayloadAction<ProductMode>) {
      state.mode = action.payload;
    },
    updateField(state, action: PayloadAction<{ field: keyof ProductFormData; value: any }>) {
      state.formData[action.payload.field] = action.payload.value;
    },
    setFieldValidation(state, action: PayloadAction<{ field: string; isValid: boolean; errorMessage: string }>) {
      state.validations[action.payload.field] = {
        isValid: action.payload.isValid,
        errorMessage: action.payload.errorMessage
      };
    },
    loadProductForEdit(state, action) {
      state.mode = action.payload.mode ?? state.mode;
      state.editingProductId = action.payload.id;
      state.formData = action.payload.formData || initialFormData;

      // ✅ Merge new validations into existing ones
      if (action.payload.validations) {
        state.validations = {
          ...state.validations,
          ...action.payload.validations,
        };
      }
    },
    resetForm(state) {
      state.formData = initialFormData;
      state.validations = {};
      state.editingProductId = null;
      state.error = null;
    },
    
    setLoading(state, action: PayloadAction<boolean>) {
      state.isLoading = action.payload;
    },
    setDialogBox(state, action: PayloadAction<boolean>) {
      state.showDialogBox = action.payload;
    },
     setBulkUploadData(state, action) {
      state.bulkUploadData = action.payload;
    },
    
    setError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
    },

    setProductType(state, action: PayloadAction<string>){
      state.productType = action.payload;
    },

    setListValues(state, action) {
      const { key, value } = action.payload;
      state[key] = value;
    }
  }
});

export const { 
  setNokiaProducts,
  setMode,
  updateField,
  setFieldValidation,
  loadProductForEdit,
  resetForm,
  setLoading,
  setDialogBox,
  setError,
  setNokiaProductsCount,
  setBulkUploadData,
  setProductType,
  setNokiaProductsTotalCount,
  setListValues
} = productMasterSlice.actions;

export default productMasterSlice.reducer;
